<template>
    <div class="text-editor">
        <div ref="type" class="text-editor-wrapper">
            <c-text-editor
                ref="editor"
                :data-editor-extensions="
                    [
                        'text-type-menu',
                        'strikethrough',
                        'highlight',
                        'link-edit-box',
                        'inline-insert-menu',
                        'mystekz-business-entities',
                        'mystekz-domain-terms',
                        'mystekz-roles',
                        'mystekz-domains',
                        'mystekz-products',
                        'mystekz-processes',
                        'code-languages',
                        'html',
                        'mermaid',
                        'table',
                        'link-preview',
                    ].join(',')
                "
                @c-text-editor:register="registerEditor"
                @c-text-editor:changed="documentChanged"
                :darkMode="ui.darkMode.value"
                class="markdown-editor"
                :readingMode="props.readingMode"
            ></c-text-editor>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";

import { Artefact } from "@/lib/artefacts";
import { useUIManager } from "@/stores/ui-store";
import "@/wc/text-editor/component/text-editor";

const artefact = defineModel<Artefact>({ required: true });
const props = withDefaults(
    defineProps<{
        readingMode?: boolean;
    }>(),
    { readingMode: false }
);

const ui = useUIManager();
const editorHooks = ref();

const registerEditor = (event: CustomEvent) => {
    editorHooks.value = event.detail.value;
    if (artefact.value.content) {
        editorHooks.value.load(artefact.value.content);
    }
};

// Sometimes the artefact is changed from the outside (e.g. when the user
// discards their changes).
watch(
    () => artefact.value.content,
    (newContent) => {
        const currentMarkdown = editorHooks.value?.getMarkdown?.();
        if (editorHooks.value && newContent !== currentMarkdown) {
            editorHooks.value.load(newContent);
        }
    },
    { immediate: true }
);

const documentModified = ref(false);
const documentChanged = () => {
    // Make sure to update the value asap, but in a separate thread. Not sure
    // why this is needed, it appears the event is triggered before the value
    // can be returned through the hooks.
    setTimeout(() => {
        if (!documentModified.value) {
            const newMarkdown = editorHooks.value?.getMarkdown?.();
            if (newMarkdown !== artefact.value.content) {
                documentModified.value = true;
            }
        }
        if (documentModified.value) {
            artefact.value.content = editorHooks.value?.getMarkdown?.();
        }
    }, 0);
};
</script>

<style lang="scss" scoped>
.text-editor-wrapper {
    display: flex;
    flex-direction: column;

    > * {
        flex: 1;
    }
}
</style>
<style lang="scss" scoped>
// We can style nodes in the document using css parts
// Current styling is only a subset of the old markdown styling
.text-editor :deep(c-text-editor) {
    &::part(node-heading-1),
    &::part(node-heading-2) {
        border-bottom: 1px solid var(--stekz-outline);
    }

    &::part(node-blockquote) {
        // Vertical padding to make the border include the child's margins
        padding: 0.01rem 1rem;
        border-left: 0.5rem solid var(--stekz-primary-color);
        background: var(--stekz-primary-background);
        margin: 0;
    }
}
</style>
